import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import SglContext from '../../SglContext';
import './associatedHorses.css';
import ReactModal from "react-modal";
import { LoadingSpinner } from '../teamManagement/utilities';

const AssociatedHorses = () => {
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [masterIds, setMasterIds] = useState([]);
  const [searchType, setSearchType] = useState('name'); // 'name' or 'membership'
  const [searchData, setSearchData] = useState({
    name: '',
    membershipType: 'USEF', // 'USEF', 'FEI', 'EC'
    membershipNumber: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const Sgl = useContext(SglContext);

  // Fetch horses data from backend
  const fetchHorses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${Sgl.nestApiUrl}/associated-horses/get-horses`,{
        params: { customer_id: Sgl.company_id, }
      });
      setHorses(response.data);
      const ids = response.data.map(horse => horse.master_id);
      setMasterIds(ids);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch horses data');
      console.error('Error fetching horses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Remove horse from backend and update state
  const handleRemoveHorse = async (master_id, horse_id, customer_id) => {
    try {
      setError(null);
      await axios.delete(`${Sgl.nestApiUrl}/associated-horses/remove-horses`, {
        params: {
          master_id,
          horse_id,
          customer_id,
        },
      });
      
      // If successful, refresh the list
      fetchHorses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove horse');
      console.error('Error removing horse:', err);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchHorses();
  }, []);

  // Retry function for error state
  const handleRetry = () => {
    fetchHorses();
  };

  // Generate initials from name
  const getInitials = (name) => {
    const first = name ? name.charAt(0).toUpperCase() : '';
    return first || '??';
  };

  // Render ID numbers
  const renderIds = (horse) => {
    const ids = [];
    if (horse.usef_number) ids.push(`USEF: ${horse.usef_number}`);
    if (horse.fei_id) ids.push(`FEI: ${horse.fei_id}`);
    if (horse.ec_sln) ids.push(`EC: ${horse.ec_sln}`);
    
    return ids.map((id, index) => (
      <span key={index} className="id-item">
        <span className="id-label">{id.split(':')[0]}:</span> {id.split(':')[1]}
      </span>
    ));
  };

  // Open search dialog
  const handleOpenSearch = () => {
    setShowSearchDialog(true);
    setSearchResults([]);
    setSearchError(null);
    setHasSearched(false);
    setSearchData({
      name: '',
      membershipType: 'USEF',
      membershipNumber: ''
    });
  };

  // Close search dialog
  const handleCloseSearch = () => {
    setShowSearchDialog(false);
    setHasSearched(false);
    setSearchResults([]);
    setSearchError(null);
    fetchHorses();
  };

  // Handle search form submission
  const handleSearchSubmit = async () => {
    setSearchResults([]);
    setHasSearched(true);
    try {
      setSearchLoading(true);
      setSearchError(null);
      
      let searchParams = {};
      if (searchType === 'name') {
        searchParams = {
          name: searchData.name
        };
      } else {
        searchParams = {
          membershipType: searchData.membershipType,
          membershipNumber: searchData.membershipNumber
        };
      }
      
      const response = await axios.get(`${Sgl.nestApiUrl}/associated-horses/search`, {
        params: {
          searchType: searchType,
          name: searchParams.name,
          membershipType: searchParams.membershipType,
          membershipNumber: searchParams.membershipNumber,
        },
      });
      
      setSearchResults(response.data);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Failed to search horses');
      console.error('Error searching horses:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Add horse from search results
  const handleAddHorse = async (horseData) => {
    try {
      setSearchError(null);
      await axios.post(`${Sgl.nestApiUrl}/associated-horses/add-horses`, {}, {
        params: {
          master_id: horseData.master_id ? horseData.master_id : 0,
          membership_type: horseData.usef_number ? 'USEF' : (horseData.fei_id ? 'FEI' : (horseData.ec_sln ? 'EC' : '')),
          membership_number: horseData.usef_number ? horseData.usef_number : (horseData.fei_id ? horseData.fei_id : (horseData.ec_sln ? horseData.ec_sln : '')),
        },
      });
      setMasterIds([...masterIds, horseData.master_id]);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Failed to add horse');
      console.error('Error adding horse:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="associated-horses-container">
        <div className="associated-horses-header">
          <h3 className="associated-horses-title">Associated Horses</h3>
        </div>
        <div className="loading-container">
          <div className="loading-text">Loading horses data...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="associated-horses-container">
        <div className="associated-horses-header">
          <h3 className="associated-horses-title">Associated Horses</h3>
        </div>
        <div className="error-container">
          <div className="error-message">Error: {error}</div>
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="associated-horses-container">
      <div className="associated-horses-header">
        <div className="title-with-refresh">
          <h3 className="associated-horses-title">Associated Horses</h3>
          <div 
            className="associated-horses-refresh-icon"
            onClick={fetchHorses}
            title="Refresh list"
          >
            <img 
              src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23000'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'%3e%3c/path%3e%3c/svg%3e"
              alt="Refresh"
            />
          </div>
        </div>
        <div className="controls">
          <button 
            className="associated-horses-search-btn"
            onClick={handleOpenSearch}
          >
            Search
          </button>
        </div>
      </div>

      {horses.length === 0 ? (
        <div className="empty-state">
          {horses.length === 0 ? 'No associated horses found!' : 'No horses match your search!'}
        </div>
      ) : (
        <div className="contacts-list">
          {horses.map((horse) => (
            <div key={`${horse.master_id}-${horse.horse_id}-${horse.customer_id}`} className="contact-item">
              <div className="contact-avatar">
                {getInitials(horse.name)}
              </div>
              
              <div className="contact-info">
                <div className="contact-name">
                  {horse.name}
                </div>
                
                {(horse.usef_number || horse.fei_id || horse.ec_sln) && (
                  <div className="contact-details">
                    {(horse.usef_number || horse.fei_id || horse.ec_sln) && (
                      <div className="contact-ids">
                        {renderIds(horse)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="contact-actions">
                {horse.master_id > 0 && (
                  <div className="verified-badge">
                    <div className="verified-icon">✓</div>
                    <span>Verified</span>
                  </div>
                )}
                
                <button
                  className="delete-btn"
                  onClick={() => handleRemoveHorse(horse.master_id, horse.horse_id, horse.customer_id)}
                  title="Remove horse"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ReactModal
        isOpen={showSearchDialog}
        onRequestClose={handleCloseSearch}
        className="modern-team-modal search-horses-dialog"
        overlayClassName="modern-team-overlay"
        ariaHideApp={false}
      >
        <div className="modern-modal-header">
          <h2 className="search-horses-modal-title">Search Horses</h2>
          <button className="modern-close-btn" onClick={handleCloseSearch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="modern-modal-body">
          {/* tabs */}
          <div className="search-type-tabs">
            <div className="tab-group">
              <div 
                className={`tab-button ${searchType === "name" ? "active" : ""}`}
                onClick={() => { 
                  setSearchType("name");
                  setHasSearched(false);
                  setSearchResults([]);
                  setSearchData({ name: '', membershipType: 'USEF', membershipNumber: ''});
                }}
              >
                Search by Name
              </div>
              <div 
                className={`tab-button ${searchType === "membership" ? "active" : ""}`}
                onClick={() => { 
                  setSearchType("membership");
                  setHasSearched(false);
                  setSearchResults([]);
                  setSearchData({ name: '', membershipType: 'USEF', membershipNumber: ''});
                }}
              >
                Search by Membership
              </div>
            </div>
          </div>

          {/* form fields */}
          {searchType === "name" ? (
            <div className="modern-form-group">
              <label>Horse Name</label>
              <input
                type="text"
                placeholder="Enter horse name"
                value={searchData.name}
                onChange={(e) => setSearchData({ ...searchData, name: e.target.value })}
                className="modern-input"
              />
            </div>
          ) : (
            <>
              <div className="modern-form-group">
                <label>Membership Type</label>
                <select
                  value={searchData.membershipType}
                  onChange={(e) => setSearchData({ ...searchData, membershipType: e.target.value })}
                  className="search-horses-membership-select"
                >
                  <option value="USEF">USEF</option>
                  <option value="FEI">FEI</option>
                  <option value="EC">EC</option>
                </select>
              </div>
              <div className="modern-form-group">
                <label>Membership Number</label>
                <input
                  type="text"
                  placeholder="Enter membership number"
                  value={searchData.membershipNumber}
                  onChange={(e) => setSearchData({ ...searchData, membershipNumber: e.target.value })}
                  className="modern-input"
                />
              </div>
            </>
          )}
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <div className="results-header-updated">
                <span className="results-title">Search Results</span>
                <span className="results-count-badge">{searchResults.length}</span>
              </div>
              <div className="results-list">
                {searchResults.map((horse) => (
                  <div key={horse.master_id} className="result-item-updated">
                    <div className="result-info">
                      <div className="result-name">{horse.name}</div>
                      {(horse.usef_number || horse.fei_id || horse.ec_sln) && (
                        <div className="contact-details">
                          <div className="contact-ids">
                            {renderIds(horse)}
                          </div>
                        </div>
                      )}
                    </div>

                    {masterIds.includes(horse.master_id) ? (
                      <div className="status-added-updated">
                        <span className="status-icon-added">✓</span>
                        <span>Added</span>
                      </div>
                    ) : (
                      <button className="add-btn-updated" onClick={() => handleAddHorse(horse)} title="Add horse">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        <span>Add</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* search button */}
        <div className="modern-modal-footer horses-modal-footer-normal">
          <button className="modern-btn-cancel" onClick={handleCloseSearch}>
            Cancel
          </button>
          <button
            className="modern-btn-primary"
            onClick={searchLoading ? null : handleSearchSubmit}
            disabled={searchLoading || (searchType === 'name' && !searchData.name) || (searchType === 'membership' && !searchData.membershipNumber)}
          >
            {searchLoading ? <LoadingSpinner message="" /> : "Search"}
          </button>
        </div>
      </ReactModal>
    </div>
  );
};

export default AssociatedHorses;